import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";
import { Pie } from "@visx/shape";

import Skeleton from "common/components/ui/Skeleton";

import { SSI_ITEMS } from "../../constants";

import type { AccountSSI } from "../../types";

const MAX_OUTER_RADIUS = 132;

const pieSortOrders = [
  "PROFESSIONAL_BRAND",
  "FIND_RIGHT_PEOPLE",
  "INSIGHT_ENGAGEMENT",
  "STRONG_RELATIONSHIP",
];

export default function SSIChart({ accountSSI }: { accountSSI: AccountSSI }) {
  const endAngle = accountSSI
    ? (Math.PI * 2 * accountSSI.overall) / 100
    : Math.PI;

  const outerRadiusScale = scaleLinear({
    domain: [
      accountSSI
        ? accountSSI.subScores.reduce(
            (acc, value) => Math.min(acc, value.score),
            0,
          )
        : 0,
      accountSSI
        ? accountSSI.subScores.reduce(
            (acc, value) => Math.max(acc, value.score),
            0,
          )
        : 0,
    ],
    range: [100, MAX_OUTER_RADIUS],
    round: true,
  });

  return (
    <div className="py-3 mx-auto relative">
      <div className="flex flex-col absolute items-center justify-center inset-0 gap-y-1.5">
        <span className="text-2xl-bold">
          {accountSSI ? (
            Math.round(accountSSI.overall)
          ) : (
            <Skeleton className="h-8 w-10" />
          )}
        </span>
        <span className="text-sm-medium text-black-500">Out of 100</span>
      </div>
      <svg width={265} height={265}>
        <title>ssi score chart</title>
        <defs>
          <linearGradient
            id="ssi-gradient"
            x1="16.0556"
            y1="-101.1111"
            x2="148.814"
            y2="86.8965"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#DCE1EA" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <Group left={265 / 2} top={265 / 2}>
          <circle
            r={90}
            fill="none"
            strokeWidth={20}
            stroke="url(#ssi-gradient)"
          />
          <circle
            r={66}
            fill="none"
            strokeWidth={8}
            stroke="url(#ssi-gradient)"
          />
          <Pie
            data={accountSSI ? accountSSI.subScores : []}
            pieValue={({ score }) => score}
            innerRadius={80}
            outerRadius={({ data: { score } }) => outerRadiusScale(score)}
            startAngle={0}
            endAngle={endAngle}
            padAngle={0.01}
            pieSort={(data1, data2) => {
              return pieSortOrders.indexOf(data1.pillar) >
                pieSortOrders.indexOf(data2.pillar)
                ? 1
                : -1;
            }}
          >
            {(pie) => {
              return pie.arcs.map((arc, index) => {
                const {
                  data: { pillar },
                } = arc;
                const arcPath = pie.path(arc);
                const fillClassName = SSI_ITEMS[pillar].fillClassName;

                return (
                  <g key={`arc-${arc.data.pillar}-${index}`}>
                    <path d={arcPath} className={fillClassName} />
                  </g>
                );
              });
            }}
          </Pie>
        </Group>
      </svg>
    </div>
  );
}
